import { useCallback, useState } from 'react'
import req from '@sportninja/common/api/request'
import { UsersMeRegistrationResponse } from '../types/usersMeRegistration'

/**
 * 🔄 useUsersMeRegistration: Fetch registration data for a specific registration ID
 *
 * A React hook that retrieves registration data from the /users/me/registrations/:uid endpoint.
 * Uses the req function internally for data fetching, error handling, and loading states.
 *
 * @param registrationUid - The unique identifier of the registration to fetch
 * @returns Object containing registration data, loading state, error state, and refetch function
 *
 * @example
 * ```tsx
 * const { data, loading, error, refetch } = useUsersMeRegistration('R8ZQVK79NJk4P6TQ');
 *
 * if (loading) return <LoadingSpinner />;
 * if (error) return <ErrorMessage error={error} />;
 * if (!data) return null;
 *
 * return (
 *   <div>
 *     <h1>{data.data.team.name}</h1>
 *     <p>Status: {data.data.status}</p>
 *   </div>
 * );
 * ```
 */
export const useUsersMeRegistration = (registrationUid?: string) => {
  // 🔄 useUsersMeRegistration: initializing hook for registration
  console.log(
    '🔄 useUsersMeRegistration: initializing hook for registration',
    registrationUid
  )

  const [data, setData] = useState<UsersMeRegistrationResponse | null>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<Error | null>(null)

  const fetchRegistration = useCallback(
    async (silent: boolean = false) => {
      if (!registrationUid) {
        setData(null)
        setLoading(false)
        setError(null)
        return
      }
      if (!silent) {
        setLoading(true)
      }
      setError(null)
      try {
        const { data: response } = await req(
          `/users/me/registrations/${registrationUid}`
        )
        setData(response)
      } catch (err) {
        setError(err as Error)
        setData(null)
      } finally {
        if (!silent) {
          setLoading(false)
        }
      }
    },
    [registrationUid]
  )

  return {
    data: registrationUid ? data : null,
    loading: registrationUid ? loading : false,
    error: registrationUid ? error : null,
    refetch: fetchRegistration,
  }
}

export default useUsersMeRegistration
